// Application state (single tiny store) + actions. Components read via hooks in ./hooks.
import { api, errorMessage } from "../api";
import type {
  ArchiveResult,
  Bucket,
  MoveItemBody,
  SpaceItems,
  StatusCategory,
  CollectionDetail,
  Manifest,
  CollectionSummary,
  CreateItem,
  Instance,
  Item,
  Me,
  Member,
  ServerEvent,
  Space,
  SyncReport,
  UpdateItem,
} from "../api/types";
import { summariseArchive } from "../lib/archive";
import { browserTimeZone, getDisplayTimeZone, setDisplayTimeZone } from "../lib/tz";
import { createStore } from "./createStore";

export interface Toast {
  id: number;
  kind: "info" | "success" | "error";
  message: string;
}

export interface AppState {
  instance: Instance | null;
  me: Me | null;
  authChecked: boolean;
  orgId: string | null;
  spaces: Space[] | null;
  currentSpaceId: string | null;
  collections: Record<string, CollectionSummary[]>; // by space id
  details: Record<string, CollectionDetail>; // by collection id
  items: Record<string, Item>; // by item id
  loadedCollections: Record<string, boolean>;
  myTasksLoaded: boolean;
  members: Member[] | null;
  toasts: Toast[];
  eventsConnected: boolean;
  /** Bumped on collection.changed to let pages refetch. */
  collectionVersion: Record<string, number>;
  /** Effective display time zone (profile, fallback browser). */
  timezone: string;
  /** Connector manifests (lazy). */
  connectors: Manifest[] | null;
  /** Archived spaces + archived collections (lazy, for the sidebar's "Archived" section). */
  archived: { spaces: Space[]; collections: CollectionSummary[] } | null;
  /** v0.4: buckets by collection id, ordered by position (lazy). */
  buckets: Record<string, Bucket[]>;
}

export const store = createStore<AppState>({
  instance: null,
  me: null,
  authChecked: false,
  orgId: null,
  spaces: null,
  currentSpaceId: null,
  collections: {},
  details: {},
  items: {},
  loadedCollections: {},
  myTasksLoaded: false,
  members: null,
  toasts: [],
  eventsConnected: false,
  collectionVersion: {},
  timezone: browserTimeZone(),
  connectors: null,
  archived: null,
  buckets: {},
});

// ---------------------------------------------------------------------------
// Toasts

let toastSeq = 1;
export function toast(message: string, kind: Toast["kind"] = "info", ttl = 5000): void {
  const id = toastSeq++;
  store.set((s) => ({ toasts: [...s.toasts, { id, kind, message }] }));
  if (ttl > 0) window.setTimeout(() => dismissToast(id), ttl);
}
export function dismissToast(id: number): void {
  store.set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
}
export function toastError(e: unknown, prefix?: string): void {
  toast(prefix ? `${prefix}: ${errorMessage(e)}` : errorMessage(e), "error", 8000);
}

export function syncReportText(r: SyncReport): string {
  const p = r.pulled;
  const q = r.pushed;
  let s = `Synced: pulled ${p.created}+/${p.updated}~/${p.deleted}−, pushed ${q.created}+/${q.updated}~/${q.deleted}−`;
  if (r.conflicts) s += `, ${r.conflicts} conflict(s)`;
  if (r.errors.length) s += `. Errors: ${r.errors.join("; ")}`;
  return s;
}

// ---------------------------------------------------------------------------
// Session

const ORG_KEY = "tia.org";
const SPACE_KEY = "tia.space";

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export async function loadInstance(): Promise<Instance> {
  const instance = await api.instance();
  store.set({ instance });
  return instance;
}

export function setMe(me: Me | null): void {
  if (!me) {
    store.set({
      me: null,
      authChecked: true,
      orgId: null,
      spaces: null,
      collections: {},
      details: {},
      items: {},
      loadedCollections: {},
      myTasksLoaded: false,
      members: null,
      archived: null,
      buckets: {},
    });
    return;
  }
  const saved = safeGet(ORG_KEY);
  const org = me.orgs.find((o) => o.org.id === saved) ?? me.orgs[0];
  setDisplayTimeZone(me.account.timezone);
  store.set({ me, authChecked: true, orgId: org?.org.id ?? null, timezone: getDisplayTimeZone() });
}

export async function checkSession(): Promise<Me | null> {
  try {
    const me = await api.me();
    setMe(me);
    return me;
  } catch {
    setMe(null);
    return null;
  }
}

export function selectOrg(orgId: string): void {
  safeSet(ORG_KEY, orgId);
  store.set({ orgId, spaces: null, collections: {}, members: null, currentSpaceId: null, archived: null });
  void loadSpaces();
}

export async function updateProfile(body: { display_name?: string; timezone?: string | null }): Promise<Me> {
  const me = await api.patchMe(body);
  setMe(me);
  void loadMembers(true).catch(() => undefined);
  return me;
}

export async function logout(): Promise<void> {
  try {
    await api.logout();
  } finally {
    setMe(null);
  }
}

export function currentMembership() {
  const s = store.get();
  return s.me?.orgs.find((o) => o.org.id === s.orgId) ?? null;
}

export function isAdmin(): boolean {
  const role = currentMembership()?.role;
  return role === "owner" || role === "admin";
}

// ---------------------------------------------------------------------------
// Spaces & collections

export async function loadSpaces(): Promise<Space[]> {
  const orgId = store.get().orgId;
  if (!orgId) return [];
  const { spaces } = await api.spaces(orgId);
  const saved = safeGet(SPACE_KEY);
  const current = spaces.find((s) => s.id === saved)?.id ?? spaces[0]?.id ?? null;
  store.set({ spaces, currentSpaceId: store.get().currentSpaceId ?? current });
  await Promise.all(spaces.map((s) => loadCollections(s.id).catch(() => undefined)));
  return spaces;
}

export function setCurrentSpace(spaceId: string): void {
  safeSet(SPACE_KEY, spaceId);
  store.set({ currentSpaceId: spaceId });
}

export async function createSpace(name: string): Promise<Space> {
  const orgId = store.get().orgId;
  if (!orgId) throw new Error("No organization selected");
  const space = await api.createSpace(orgId, name);
  store.set((s) => ({
    spaces: [...(s.spaces ?? []), space],
    collections: { ...s.collections, [space.id]: [] },
  }));
  void loadCollections(space.id);
  return space;
}

export async function loadCollections(spaceId: string): Promise<CollectionSummary[]> {
  const { collections } = await api.collections(spaceId);
  store.set((s) => ({ collections: { ...s.collections, [spaceId]: collections } }));
  return collections;
}

export async function loadCollection(id: string): Promise<CollectionDetail> {
  const detail = await api.collection(id);
  store.set((s) => ({ details: { ...s.details, [id]: detail } }));
  return detail;
}

export async function createCollection(
  spaceId: string,
  name: string,
  kind: CollectionSummary["kind"],
): Promise<CollectionDetail> {
  const detail = await api.createCollection(spaceId, { name, kind });
  store.set((s) => ({ details: { ...s.details, [detail.collection.id]: detail } }));
  await loadCollections(spaceId);
  return detail;
}

export function findCollection(id: string): CollectionSummary | undefined {
  for (const list of Object.values(store.get().collections)) {
    const c = list.find((x) => x.id === id);
    if (c) return c;
  }
  return undefined;
}

export function allCollections(): CollectionSummary[] {
  return Object.values(store.get().collections).flat();
}

// ---------------------------------------------------------------------------
// v0.3: rename / archive / delete spaces & collections

let connectorsPromise: Promise<Manifest[]> | null = null;
export function loadConnectors(): Promise<Manifest[]> {
  const cached = store.get().connectors;
  if (cached) return Promise.resolve(cached);
  connectorsPromise ??= api
    .connectors()
    .then(({ connectors }) => {
      store.set({ connectors });
      return connectors;
    })
    .catch(() => [] as Manifest[])
    .finally(() => {
      connectorsPromise = null;
    });
  return connectorsPromise;
}

/** Load archived spaces (with their collections) and archived collections of active spaces. */
export async function loadArchived(): Promise<void> {
  const orgId = store.get().orgId;
  if (!orgId) return;
  const { spaces } = await api.spaces(orgId, true);
  const lists = await Promise.all(
    spaces.map((sp) =>
      api
        .collections(sp.id, true)
        .then((r) => r.collections)
        .catch(() => [] as CollectionSummary[]),
    ),
  );
  const archivedSpaces = spaces.filter((sp) => !!sp.archived_at);
  const archivedSpaceIds = new Set(archivedSpaces.map((sp) => sp.id));
  const collections = lists
    .flat()
    .filter((c) => !!c.archived_at || archivedSpaceIds.has(c.space_id));
  store.set({ archived: { spaces: archivedSpaces, collections } });
}

function refreshArchivedIfLoaded(): void {
  if (store.get().archived) void loadArchived().catch(() => undefined);
}

function dropSpace(spaceId: string): void {
  store.set((s) => {
    const collections = { ...s.collections };
    delete collections[spaceId];
    const spaces = (s.spaces ?? []).filter((x) => x.id !== spaceId);
    const currentSpaceId = s.currentSpaceId === spaceId ? (spaces[0]?.id ?? null) : s.currentSpaceId;
    return { spaces, collections, currentSpaceId };
  });
}

export async function renameSpace(id: string, name: string): Promise<Space> {
  const space = await api.patchSpace(id, { name });
  store.set((s) => ({ spaces: (s.spaces ?? []).map((x) => (x.id === id ? { ...x, ...space } : x)) }));
  return space;
}

export async function renameCollection(id: string, spaceId: string, name: string): Promise<CollectionDetail> {
  const detail = await api.patchCollection(id, { name });
  store.set((s) => ({ details: { ...s.details, [id]: detail } }));
  await loadCollections(spaceId).catch(() => undefined);
  return detail;
}

function collectionLabel(id: string, fallback = "collection"): string {
  const c = findCollection(id) ?? store.get().archived?.collections.find((x) => x.id === id);
  return c ? `"${c.name}"` : fallback;
}

/** Archive / unarchive a collection; toasts a summary of what happened remotely. */
export async function setCollectionArchived(id: string, archived: boolean, remote = true): Promise<ArchiveResult> {
  const label = collectionLabel(id);
  const spaceId = findCollection(id)?.space_id ?? store.get().archived?.collections.find((x) => x.id === id)?.space_id;
  const res = archived ? await api.archiveCollection(id, remote) : await api.unarchiveCollection(id, remote);
  store.set((s) => ({ details: { ...s.details, [id]: res.collection } }));
  const sid = spaceId ?? res.collection.collection.space_id;
  if (sid) await loadCollections(sid).catch(() => undefined);
  refreshArchivedIfLoaded();
  const manifests = await loadConnectors();
  const sum = summariseArchive([res], archived ? "archive" : "unarchive", label, manifests);
  toast(sum.message, sum.kind, sum.lines.length ? 9000 : 5000);
  return res;
}

export async function setSpaceArchived(id: string, archived: boolean, remote = true): Promise<void> {
  const name =
    store.get().spaces?.find((x) => x.id === id)?.name ?? store.get().archived?.spaces.find((x) => x.id === id)?.name;
  const res = archived ? await api.archiveSpace(id, remote) : await api.unarchiveSpace(id, remote);
  if (archived) dropSpace(id);
  else {
    store.set((s) => ({
      spaces: [...(s.spaces ?? []).filter((x) => x.id !== id), res.space],
    }));
    await loadCollections(id).catch(() => undefined);
  }
  for (const c of res.collections) {
    store.set((s) => ({ details: { ...s.details, [c.collection.collection.id]: c.collection } }));
  }
  refreshArchivedIfLoaded();
  const manifests = await loadConnectors();
  const n = res.collections.length;
  const subject = `space "${name ?? res.space.name}" (${n} collection${n === 1 ? "" : "s"})`;
  const sum = summariseArchive(res.collections, archived ? "archive" : "unarchive", subject, manifests);
  toast(sum.message, sum.kind, sum.lines.length ? 9000 : 5000);
}

export async function deleteCollectionById(id: string): Promise<void> {
  const spaceId = findCollection(id)?.space_id ?? store.get().archived?.collections.find((x) => x.id === id)?.space_id;
  await api.deleteCollection(id);
  store.set((s) => {
    const details = { ...s.details };
    delete details[id];
    const items = Object.fromEntries(Object.entries(s.items).filter(([, it]) => it.collection_id !== id));
    return { details, items };
  });
  if (spaceId) await loadCollections(spaceId).catch(() => undefined);
  refreshArchivedIfLoaded();
}

export async function deleteSpaceById(id: string): Promise<void> {
  await api.deleteSpace(id);
  dropSpace(id);
  store.set((s) => ({
    items: Object.fromEntries(Object.entries(s.items).filter(([, it]) => it.space_id !== id)),
  }));
  refreshArchivedIfLoaded();
}

// ---------------------------------------------------------------------------
// Items

function sameItem(a: Item, b: Item): boolean {
  return (
    a.status_id === b.status_id &&
    a.position === b.position &&
    a.title === b.title &&
    JSON.stringify(a.sync) === JSON.stringify(b.sync)
  );
}

export function upsertItems(items: Item[]): void {
  if (items.length === 0) return;
  store.set((s) => {
    let next: Record<string, Item> | null = null;
    for (const it of items) {
      const prev = s.items[it.id];
      // Keep the newest version (realtime events may race HTTP responses); skip no-op updates
      // so subscribers are not woken up needlessly.
      if (prev && (it.version < prev.version || (it.version === prev.version && it.updated_at === prev.updated_at && sameItem(prev, it)))) {
        continue;
      }
      next ??= { ...s.items };
      next[it.id] = it;
    }
    return next ? { items: next } : {};
  });
}

export function removeItem(id: string): void {
  store.set((s) => {
    if (!s.items[id]) return {};
    const next = { ...s.items };
    delete next[id];
    return { items: next };
  });
}

export async function loadItems(collectionId: string): Promise<Item[]> {
  const { items } = await api.items(collectionId, { include_done: true });
  store.set((s) => {
    const next = { ...s.items };
    // Drop stale items of this collection that the server no longer returns.
    for (const [id, it] of Object.entries(next)) if (it.collection_id === collectionId) delete next[id];
    for (const it of items) next[it.id] = it;
    return { items: next, loadedCollections: { ...s.loadedCollections, [collectionId]: true } };
  });
  return items;
}

export async function loadMyTasks(includeDone = false): Promise<Item[]> {
  const { items } = await api.myTasks(includeDone);
  upsertItems(items);
  store.set({ myTasksLoaded: true });
  return items;
}

export async function createItem(collectionId: string, body: CreateItem): Promise<Item> {
  const item = await api.createItem(collectionId, body);
  upsertItems([item]);
  return item;
}

/** Optimistic PATCH with rollback. */
export async function patchItem(id: string, patch: UpdateItem, optimistic: Partial<Item> = {}): Promise<Item> {
  const prev = store.get().items[id];
  if (prev) {
    const { body: _b, meeting: _m, fields, ...rest } = patch;
    void _b;
    void _m;
    const local: Partial<Item> = { ...(rest as Partial<Item>), ...optimistic };
    if (fields) local.fields = fields;
    store.set((s) => ({ items: { ...s.items, [id]: { ...prev, ...local } } }));
  }
  try {
    const item = await api.patchItem(id, patch);
    store.set((s) => ({ items: { ...s.items, [id]: item } }));
    return item;
  } catch (e) {
    if (prev) store.set((s) => ({ items: { ...s.items, [id]: prev } }));
    throw e;
  }
}

/**
 * Optimistic move with rollback. The target is a status (`status_id` + its category), a status
 * category alone (space board: the server picks the status) and/or a bucket (`bucket_id`).
 */
export async function moveItem(
  id: string,
  args: {
    status_id?: string;
    status_category?: StatusCategory;
    bucket_id?: string | null;
    after_id: string | null;
    before_id: string | null;
    position: string;
  },
): Promise<Item> {
  const prev = store.get().items[id];
  const local: Partial<Item> = { position: args.position };
  const body: MoveItemBody = { after_id: args.after_id, before_id: args.before_id };
  if (args.status_id) {
    body.status_id = args.status_id;
    local.status_id = args.status_id;
  } else if (args.status_category) body.status_category = args.status_category;
  if (args.status_category) local.status_category = args.status_category;
  if (args.bucket_id !== undefined) body.bucket_id = local.bucket_id = args.bucket_id;
  if (prev) store.set((s) => ({ items: { ...s.items, [id]: { ...prev, ...local } } }));
  try {
    const item = await api.moveItem(id, body);
    store.set((s) => ({ items: { ...s.items, [id]: item } }));
    return item;
  } catch (e) {
    if (prev) store.set((s) => ({ items: { ...s.items, [id]: prev } }));
    throw e;
  }
}

export async function deleteItem(id: string): Promise<void> {
  const prev = store.get().items[id];
  removeItem(id);
  try {
    await api.deleteItem(id);
  } catch (e) {
    if (prev) upsertItems([prev]);
    throw e;
  }
}

// ---------------------------------------------------------------------------
// Members

let membersPromise: Promise<Member[]> | null = null;
export function loadMembers(force = false): Promise<Member[]> {
  const orgId = store.get().orgId;
  if (!orgId) return Promise.resolve([]);
  if (!force && store.get().members) return Promise.resolve(store.get().members!);
  if (!force && membersPromise) return membersPromise;
  membersPromise = api
    .members(orgId)
    .then(({ members }) => {
      store.set({ members });
      return members;
    })
    .finally(() => {
      membersPromise = null;
    });
  return membersPromise;
}

// ---------------------------------------------------------------------------
// Realtime events

export function applyServerEvent(ev: ServerEvent): void {
  switch (ev.type) {
    case "item.changed":
      upsertItems([ev.item]);
      break;
    case "item.deleted":
      removeItem(ev.item_id);
      break;
    case "collection.changed":
      store.set((s) => ({
        collectionVersion: { ...s.collectionVersion, [ev.collection_id]: (s.collectionVersion[ev.collection_id] ?? 0) + 1 },
      }));
      if (store.get().details[ev.collection_id]) void loadCollection(ev.collection_id).catch(() => undefined);
      // ASSUMPTION: bucket changes (also remote ones from sync) are announced as collection.changed.
      if (store.get().buckets[ev.collection_id]) void loadBuckets(ev.collection_id).catch(() => undefined);
      {
        const c = findCollection(ev.collection_id);
        if (c) void loadCollections(c.space_id).catch(() => undefined);
      }
      break;
    case "sync.finished":
      // Bindings' last_synced_at changed; refresh details that contain this binding.
      for (const d of Object.values(store.get().details)) {
        if (d.bindings.some((b) => b.id === ev.report.binding_id)) {
          void loadCollection(d.collection.id).catch(() => undefined);
        }
      }
      break;
  }
}

/** Delete an item and the chosen subset of the items created from its document. */
export async function deleteItemWithCreated(id: string, deleteIds: string[]): Promise<void> {
  await api.deleteItemWithCreated(id, deleteIds);
  for (const x of [id, ...deleteIds]) removeItem(x);
}

// ---------------------------------------------------------------------------
// v0.4: space overview

const TARGET_KEY = "tia.spaceTarget.";
/** Last collection used for quick-add in a space overview (per browser). */
export const savedSpaceTarget = (spaceId: string) => safeGet(TARGET_KEY + spaceId);
export const saveSpaceTarget = (spaceId: string, collectionId: string) => safeSet(TARGET_KEY + spaceId, collectionId);

/** Load all items of a space (non-archived collections) into the item store. */
export async function loadSpaceItems(spaceId: string, includeDone: boolean): Promise<SpaceItems> {
  const res = await api.spaceItems(spaceId, { include_done: includeDone });
  upsertItems(res.items);
  return res;
}

// ---------------------------------------------------------------------------
// v0.4: buckets

const byBucketPosition = (a: Bucket, b: Bucket) => a.position - b.position || a.name.localeCompare(b.name);

function setBuckets(collectionId: string, update: (list: Bucket[]) => Bucket[]): void {
  store.set((s) => ({
    buckets: { ...s.buckets, [collectionId]: update(s.buckets[collectionId] ?? []).sort(byBucketPosition) },
  }));
}

export async function loadBuckets(collectionId: string): Promise<Bucket[]> {
  const { buckets } = await api.buckets(collectionId);
  setBuckets(collectionId, () => [...buckets]);
  return buckets;
}

/** Enable / disable buckets; enabling creates them server-side (from stacks / lists) and assigns items. */
export async function setBucketsEnabled(collectionId: string, enabled: boolean): Promise<CollectionDetail> {
  const detail = await api.patchCollection(collectionId, { buckets_enabled: enabled });
  store.set((s) => ({ details: { ...s.details, [collectionId]: detail } }));
  await Promise.all([
    loadCollections(detail.collection.space_id).catch(() => undefined),
    enabled ? loadBuckets(collectionId).catch(() => undefined) : undefined,
    // Enabling assigns bucket_id (and for Deck can change statuses): refresh items when shown.
    store.get().loadedCollections[collectionId] ? loadItems(collectionId).catch(() => undefined) : undefined,
  ]);
  return detail;
}

export async function createBucket(collectionId: string, name: string, position?: number): Promise<Bucket> {
  const bucket = await api.createBucket(collectionId, position === undefined ? { name } : { name, position });
  setBuckets(collectionId, (list) => [...list.filter((b) => b.id !== bucket.id), bucket]);
  return bucket;
}

export async function renameBucket(bucket: Bucket, name: string): Promise<Bucket> {
  const updated = await api.patchBucket(bucket.id, { name });
  setBuckets(bucket.collection_id, (list) => list.map((b) => (b.id === bucket.id ? updated : b)));
  return updated;
}

/** Optimistic reorder with rollback. */
export async function moveBucket(bucket: Bucket, position: number, order: string[]): Promise<Bucket> {
  const prev = store.get().buckets[bucket.collection_id];
  setBuckets(bucket.collection_id, (list) =>
    list.map((b) => ({ ...b, position: order.includes(b.id) ? order.indexOf(b.id) : b.position })),
  );
  try {
    const updated = await api.patchBucket(bucket.id, { position });
    await loadBuckets(bucket.collection_id);
    return updated;
  } catch (e) {
    if (prev) setBuckets(bucket.collection_id, () => [...prev]);
    throw e;
  }
}

/** Delete a bucket; its items move to `moveTo` (null = no bucket) first. */
export async function deleteBucket(bucket: Bucket, moveTo: string | null): Promise<void> {
  await api.deleteBucket(bucket.id, moveTo);
  setBuckets(bucket.collection_id, (list) => list.filter((b) => b.id !== bucket.id));
  store.set((s) => {
    let next: Record<string, Item> | null = null;
    for (const it of Object.values(s.items)) {
      if (it.bucket_id !== bucket.id) continue;
      next ??= { ...s.items };
      next[it.id] = { ...it, bucket_id: moveTo };
    }
    return next ? { items: next } : {};
  });
  void loadItems(bucket.collection_id).catch(() => undefined);
}
